import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import Scrollbars from 'react-custom-scrollbars';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import { axiosPath, path } from '../../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import colors from '../../../../../../../constants/colors';
import { getTotalEnergiesRiskRiskFilterInitialValues } from '../../../../../../../minions/forms';
import ReviewFields from './ReviewFields';
import ReviewEdit from './ReviewEdit';
import { getReviewState } from '../../../tools/totalEnergiesRiskActionPlanTools';

export default function ReviewTab({ elementID }) {
  const { moduleBridgeHex, filtersHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const reviews = useSelector((s) => s.currentPage?.reviewTab?.[elementID]);
  const reviewID = useSelector((s) => s.currentPage?.reviewID);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (filtersHex !== '0' && reviews?.length > 0 && reviewID) {
      setSelectedReview(reviews.find((r) => r?.id === reviewID));
      dispatch(currentPageAction.change('reviewID', undefined));
    }
  }, [reviews]);

  // > LOAD REVIEW TAB
  const loadReviews = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_totalEnergiesRisk_actionPlan_review_tab',
          ),
          { moduleBridgeHex, actionPlanID: elementID },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('reviewTab', {
                [elementID]: data,
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID],
  );

  const goToLinkedRisks = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_totalEnergiesRisk_actionPlan_review_goToLinkedRisks',
          ),
          {
            moduleBridgeHex,
            actionPlanID: elementID,
            filters: getTotalEnergiesRiskRiskFilterInitialValues(),
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(
              path({
                name: 'user_module_totalEnergiesRisk_risk_list',
                params: {
                  moduleBridgeHex,
                  page: 1,
                  filtersHex: data?.hexCode,
                },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID, getTotalEnergiesRiskRiskFilterInitialValues],
  );

  useEffect(() => {
    if (isLoading) loadReviews();
  }, [isLoading]);
  // < LOAD REVIEW TAB

  const showEditBtn = useCallback(
    () =>
      (selectedReview?.state === 1 &&
        selectedReview?.responsibleID === userBridge?.id) ||
      (selectedReview?.state === 2 && userBridge?.permissions?.level === 0) ||
      (userBridge?.permissions?.level === 0 &&
        (selectedReview?.state === 0 || selectedReview?.state === 2)) ||
      (selectedReview?.responsibleID === userBridge?.id &&
        selectedReview?.state === 1),
    [selectedReview, userBridge],
  );

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <Container>
      <SidebarMenu>
        {reviews?.length > 0 ? (
          <Scrollbars style={{ height: '365px' }}>
            <ul>
              {reviews.length > 0 &&
                reviews.map((r) => (
                  <li
                    title={getReviewState(r?.state)}
                    style={{
                      borderLeftColor:
                        colors.totalEnergiesRiskControlState[r?.state],
                    }}
                    key={r.id}
                    className={`review ${
                      selectedReview?.id === r.id ? 'selected' : ''
                    }`}
                    onClick={() => {
                      setShowEdit(false);
                      setSelectedReview(r);
                    }}
                  >
                    <div className="first-row">{r?.scheduledReviewDate}</div>
                  </li>
                ))}
            </ul>
          </Scrollbars>
        ) : (
          <AppNoInfo
            text="Sin revisiones"
            color={colors.primaryLight}
            icon={
              <AppRemixIcon
                size={24}
                icon="file-search"
                color={colors.primaryLight}
              />
            }
          />
        )}
      </SidebarMenu>
      <Content>
        {selectedReview ? (
          <>
            {showEdit ? (
              <>
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6">
                      <h3>Revisión {selectedReview?.scheduledReviewDate}</h3>
                    </div>
                    <div className="col-lg-6 d-flex justify-content-end">
                      <AppRemixIcon
                        icon="external-link"
                        className="pointer mr-2"
                        title="Ir a riesgos asociados"
                        size={16}
                        onClick={goToLinkedRisks}
                      />
                      <AppRemixIcon
                        icon="close"
                        className="pointer"
                        size={16}
                        onClick={() => {
                          setShowEdit(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <ReviewEdit
                  elementID={elementID}
                  review={selectedReview}
                  onSave={() => {
                    setSelectedReview(null);
                    setShowEdit(false);
                    setIsLoading(true);
                  }}
                />
              </>
            ) : (
              <>
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6">
                      <h3>Revisión {selectedReview?.scheduledReviewDate}</h3>
                    </div>
                    <div className="col-lg-6 d-flex justify-content-end">
                      {showEditBtn() && (
                        <AppRemixIcon
                          icon="edit"
                          className="pointer mr-2"
                          title="Editar"
                          size={16}
                          onClick={() => setShowEdit(true)}
                        />
                      )}
                      <AppRemixIcon
                        icon="external-link"
                        className="pointer mr-2"
                        title="Ir a riesgos asociados"
                        size={16}
                        onClick={goToLinkedRisks}
                      />
                      <AppRemixIcon
                        icon="close"
                        className="pointer"
                        title="Cerrar"
                        size={16}
                        onClick={() => {
                          setSelectedReview(null);
                          setShowEdit(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <ReviewFields
                  values={selectedReview}
                  files={selectedReview?.files}
                  isReadOnly
                />
              </>
            )}
          </>
        ) : (
          <AppNoInfo
            text="Sin revisión seleccionada"
            color={colors.primaryLight}
            icon={
              <AppRemixIcon
                size={24}
                icon="file-search"
                color={colors.primaryLight}
              />
            }
          />
        )}
      </Content>
    </Container>
  );
}

const Container = styled.div`
  height: 455px;
  display: grid;
  grid-template-columns: 260px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      &.review {
        border-left-width: 6px;
        border-left-style: solid;
        .first-row {
          padding-left: 6px;
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: 235px;
        }
      }
      padding: 4px 6px 4px 4px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 455px;
  .row-no-margin > .row {
    margin: 0;
  }
  h3 {
    font-size: 13px;
    margin: 0px 0px 6px;
    font-weight: bold;
  }
`;
